import React from 'react';

import { formatHours } from './formatHours';

// GRAVY-10073: per-row "Hours This Week" indicator on the staffing worker
// list. Renders `N/40 hrs`. The whole string (worker-hours value AND the
// `/40 hrs` threshold suffix) flips to red together when the worker has
// already exceeded 40 OR when the admin's selection would tip them over in
// the order's target payroll week. The two parts share one color so the
// cell reads as a single unit per the approved UI design.
//
// `hoursThisWeek` is the worker's existing hours in the order's TARGET
// payroll week (the backend anchors it to the order's impacted week, taking
// the max across impacted weeks). `selectedShiftHours` is the wizard's MAX
// per-week net total of the selected shifts, bucketed by the same Eastern
// payroll week — so both sides describe the same target week and a clean
// future-week over-40 hire turns red, while overtime in an unrelated week
// does not.
//
// `hoursThisWeek` is `null` for 1099 workers (the OT projection is W2-only
// on the backend) — we render an em-dash so the column layout stays
// consistent without implying a meaningful zero.

const FONT = "'Manrope', sans-serif";
const RED = '#D14848';
const NEUTRAL = '#1C1C1C';
const OT_THRESHOLD = 40;

type Props = {
  hoursThisWeek: number | null | undefined;
  /**
   * MAX per-week net hours of the currently-selected shifts, bucketed by
   * Eastern payroll week (NOT the cumulative total of every selected shift).
   * Anchored to the same target week as `hoursThisWeek` so the projected
   * comparison stays within a single payroll week. Used only for the legacy
   * scalar fallback when `hoursThisWeekByWeek` is absent.
   */
  selectedShiftHours?: number;
  /**
   * Per-week existing hours for the order's TARGET payroll weeks (backend),
   * keyed by Eastern-Monday `YYYY-MM-DD`. When provided, the at-risk red is
   * decided ONE WEEK AT A TIME (`existing[w] + selected[w] > 40`) instead of
   * comparing the worst existing week against the worst selected week — which
   * double-counted hours from DIFFERENT weeks and produced false positives.
   */
  hoursThisWeekByWeek?: Array<{ week_start: string; hours: number }> | null;
  /**
   * Per-week net hours of the currently-selected shifts, keyed by the same
   * Eastern-Monday `YYYY-MM-DD` payroll-week key as `hoursThisWeekByWeek`.
   */
  selectedShiftHoursByWeek?: Record<string, number>;
};

// Red when any single payroll week's existing + selected hours exceed 40.
// Joins the backend's per-week existing hours with the wizard's per-week
// selected hours by their shared Eastern-Monday key, so a worker who is near
// the cap in two different weeks (but over in neither) no longer reads red.
const isAtRiskPerWeek = (
  existing: Array<{ week_start: string; hours: number }>,
  selected: Record<string, number>,
): boolean => {
  const totalsByWeek: Record<string, number> = { ...selected };
  for (const week of existing) {
    totalsByWeek[week.week_start] =
      (totalsByWeek[week.week_start] ?? 0) + week.hours;
  }
  return Object.values(totalsByWeek).some((hours) => hours > OT_THRESHOLD);
};

export const HoursThisWeekCell: React.FC<Props> = ({
  hoursThisWeek,
  selectedShiftHours = 0,
  hoursThisWeekByWeek,
  selectedShiftHoursByWeek,
}) => {
  if (hoursThisWeek == null) {
    return (
      <span
        data-testid="hours-this-week-cell"
        style={{ fontFamily: FONT, color: NEUTRAL }}
      >
        &mdash;
      </span>
    );
  }
  // Red when any single payroll week's existing + selected hours exceed 40 —
  // covering both an already-over worker (selected = 0) and an about-to-exceed
  // action. A worker exactly at 40 with no selection stays neutral (40 is not
  // "exceeding"). Prefer the per-week join when the backend ships the
  // breakdown; fall back to the legacy scalar sum (max-existing + max-selected)
  // only when it's absent (e.g. a caller that doesn't wire per-week props yet).
  const atRisk =
    hoursThisWeekByWeek != null
      ? isAtRiskPerWeek(hoursThisWeekByWeek, selectedShiftHoursByWeek ?? {})
      : hoursThisWeek + selectedShiftHours > OT_THRESHOLD;
  // Color is set on BOTH the outer span and the inner <strong> tag. Browser
  // defaults and MUI's CSS resets can override `color` inheritance on
  // <strong> in some surfaces, so setting it only on the outer span leaves
  // the worker-hours number in the default color while the "/40 hrs" suffix
  // turns red. Explicit color on both keeps the full string unified.
  const color = atRisk ? RED : NEUTRAL;
  return (
    <span
      data-testid="hours-this-week-cell"
      data-at-risk={atRisk ? 'true' : 'false'}
      style={{
        fontFamily: FONT,
        fontWeight: 600,
        color,
      }}
    >
      <strong data-testid="hours-this-week-value" style={{ color }}>
        {formatHours(hoursThisWeek)}
      </strong>
      /{OT_THRESHOLD} hrs
    </span>
  );
};

export default HoursThisWeekCell;
